// quantity-bundle.js
// Funcionalidade para o Bundle de Quantidade

class QuantityBundle {
  constructor(container) {
    this.container = container;
    this.productId = container.dataset.productId;
    this.options = Array.from(container.querySelectorAll('.quantity-bundle__option'));
    this.radioButtons = container.querySelectorAll('.quantity-bundle__radio');
    this.buyButton = container.querySelector('.quantity-bundle__buy-button');
    this.comparePriceEl = container.querySelector('.quantity-bundle__compare-price');
    this.currentPriceEl = container.querySelector('.quantity-bundle__current-price');
    this.pixPriceEl = container.querySelector('.quantity-bundle__pix-price');
    this.summaryPriceEachEl = container.querySelector('.quantity-bundle__pricing .price-each');
    this.discountBadgeEl = container.querySelector('.quantity-bundle__pricing .tp-badge');
    this.installmentWrapper = container.querySelector('[data-installment-wrapper]');
    this.installmentCountEl = this.installmentWrapper?.querySelector('[data-installment-count]') || null;
    this.installmentValueEl = this.installmentWrapper?.querySelector('[data-installment-value]') || null;
    
    this.baseComparePrice = parseFloat(this.comparePriceEl.dataset.comparePrice) / 100;
    this.baseCurrentPrice = parseFloat(this.currentPriceEl.dataset.currentPrice) / 100;
    const pixDiscountAttr = this.pixPriceEl?.dataset?.pixDiscount;
    const parsedPixDiscount = pixDiscountAttr ? parseFloat(pixDiscountAttr) : NaN;
    this.pixDiscount = Number.isFinite(parsedPixDiscount) ? parsedPixDiscount : 0;
    this.pixPriceSuffix = this.pixPriceEl ? this.pixPriceEl.textContent.trim().split(' ').slice(-2).join(' ') : '';
    this.maxInstallments = parseInt(container.dataset.maxInstallments || '0', 10);
    this.minInstallmentCents = parseInt(container.dataset.minInstallment || '0', 10);
    
    // Capturar descontos por quantidade - versão dinâmica para Horizon
    this.quantityDiscounts = {};
    this.optionLinks = {};
    
    // Capturar dados dinamicamente de cada opção disponível
    this.options.forEach(option => {
      const quantity = parseInt(option.dataset.quantity, 10);
      if (!quantity) {
        return;
      }

      const discountValue = parseFloat(option.dataset.discountValue) || 0;
      this.quantityDiscounts[quantity] = discountValue;

      const optionLink = (option.dataset.link || '').trim();
      this.optionLinks[quantity] = optionLink;
    });
    
    // Debug temporário - remover em produção
    // console.log('Mapeamento dinâmico de descontos:', this.quantityDiscounts);
    
    this.selectedQuantity = this.getSelectedQuantity();
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.updatePrices();
    this.setupButton();
  }

  debugDiscounts() {
    // Debug para verificar se os descontos estão sendo capturados corretamente
    console.log('=== DEBUG QUANTITY BUNDLE ===');
    console.log('Container dataset completo:', this.container.dataset);
    console.log('option1Discount:', this.container.dataset.option1Discount);
    console.log('option2Discount:', this.container.dataset.option2Discount);
    console.log('option3Discount:', this.container.dataset.option3Discount);
    console.log('Descontos capturados:', this.quantityDiscounts);
    console.log('Preço base:', this.baseCurrentPrice);
    console.log('=============================');
  }

  setupButton() {
    // Salvar o texto e estilo original do botão
    this.originalButtonText = this.buyButton.textContent;
    this.originalButtonStyle = this.buyButton.style.background;
  }

  bindEvents() {
    // Event listener para mudança de quantidade
    this.radioButtons.forEach(radio => {
      radio.addEventListener('change', () => {
        this.selectedQuantity = this.getSelectedQuantity();
        this.updatePrices();
        this.updateVisualSelection();
      });
    });

    // Event listener para botão de compra
    this.buyButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleButtonClick();
    });

    // Event listener para clique nas opções
    this.options.forEach(option => {
      option.addEventListener('click', (e) => {
        if (e.target.type !== 'radio') {
          const radio = option.querySelector('.quantity-bundle__radio');
          if (radio) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change'));
          }
        }
      });
    });
  }

  handleButtonClick() {
    if (this.selectedQuantity === null) {
      return;
    }

    const selectedLink = this.getSelectedLink();
    if (selectedLink) {
      window.location.href = selectedLink;
      return;
    }

    this.addToCart();
  }

  getSelectedQuantity() {
    const checkedRadio = this.container.querySelector('.quantity-bundle__radio:checked');
    return checkedRadio ? parseInt(checkedRadio.value) : null;
  }

  getSelectedLink() {
    const quantity = this.selectedQuantity;
    const link = this.optionLinks[quantity];

    if (typeof link === 'string') {
      const trimmedLink = link.trim();
      if (trimmedLink.length > 0) {
        return trimmedLink;
      }
    }

    return null;
  }

  updatePrices() {
    const quantity = this.selectedQuantity;
    if (quantity === null) {
      return;
    }
    
    const selectedOption = this.getSelectedOption();
    const optionDisplayData = this.getOptionDisplayData(selectedOption);

    // Obter desconto para a quantidade selecionada
    const quantityDiscountPercent = (optionDisplayData.discountValue ?? this.quantityDiscounts[quantity]) ?? 0;
    const quantityDiscountDecimal = quantityDiscountPercent / 100;
    
    // Calcular preços totais
    const totalCurrentPriceBeforeDiscount = this.baseCurrentPrice * quantity;
    
    // Aplicar desconto por quantidade
    const totalCurrentPrice = totalCurrentPriceBeforeDiscount * (1 - quantityDiscountDecimal);
    
    // Aplicar desconto do Pix em cima do preço já com desconto de quantidade
    const pixDiscountDecimal = this.pixDiscount / 100;
    const totalPixPrice = totalCurrentPrice * (1 - pixDiscountDecimal);
    const priceEachValue = totalCurrentPrice / quantity;

    // Atualizar elementos no DOM
    this.updateComparePrice(optionDisplayData.totalCompareText);
    const currentText = optionDisplayData.totalPriceText || this.formatPrice(totalCurrentPrice);
    this.currentPriceEl.textContent = currentText;

    this.updateSummaryPriceEach(priceEachValue, optionDisplayData.priceEachText);
    this.updateDiscountBadge(quantityDiscountPercent);
    
    // Atualizar preço do Pix mantendo o texto original
    if (this.pixPriceEl) {
      const pixText = this.pixPriceSuffix || this.pixPriceEl.textContent.split(' ').slice(-2).join(' ');
      this.pixPriceEl.innerHTML = `${this.formatPrice(totalPixPrice)} ${pixText}`.trim();
    }

    this.updateInstallments(totalCurrentPrice);
  }

  updateVisualSelection() {
    // Remove seleção visual de todas as opções
    this.container.querySelectorAll('.quantity-bundle__option').forEach(option => {
      option.classList.remove('quantity-bundle__option--selected');
    });

    // Adiciona seleção visual à opção ativa
    const selectedRadio = this.container.querySelector('.quantity-bundle__radio:checked');
    if (selectedRadio) {
      const selectedOption = selectedRadio.closest('.quantity-bundle__option');
      selectedOption.classList.add('quantity-bundle__option--selected');
    }
  }

  getSelectedOption() {
    const selectedRadio = this.container.querySelector('.quantity-bundle__radio:checked');
    return selectedRadio ? selectedRadio.closest('.quantity-bundle__option') : null;
  }

  getOptionDisplayData(optionElement) {
    if (!optionElement) {
      return {};
    }

    return {
      priceEachText: this.normalizeText(optionElement.dataset.priceEachText),
      totalCompareText: this.normalizeText(optionElement.dataset.totalCompareText),
      totalPriceText: this.normalizeText(optionElement.dataset.totalPriceText),
      discountValue: this.parseNumber(optionElement.dataset.discountValue)
    };
  }

  normalizeText(value) {
    if (typeof value !== 'string') {
      return '';
    }
    return value.trim();
  }

  parseNumber(value) {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  appendCada(text) {
    if (typeof text !== 'string') {
      return '';
    }

    const trimmed = text.trim();
    if (!trimmed) {
      return '';
    }

    if (trimmed.toLowerCase().includes('cada')) {
      return trimmed;
    }

    return `${trimmed} cada`.trim();
  }

  updateComparePrice(displayText) {
    if (!this.comparePriceEl) {
      return;
    }

    if (displayText) {
      this.comparePriceEl.textContent = displayText;
      this.comparePriceEl.removeAttribute('hidden');
    } else {
      this.comparePriceEl.textContent = '';
      this.comparePriceEl.setAttribute('hidden', 'hidden');
    }
  }

  formatPrice(price) {
    return 'R$' + price.toFixed(2).replace('.', ',');
  }
  formatPercentage(percentValue) {
    if (!Number.isFinite(percentValue) || percentValue <= 0) {
      return '0';
    }
    return percentValue.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  updateSummaryPriceEach(unitPrice, textOverride) {
    if (!this.summaryPriceEachEl) {
      return;
    }

    if (textOverride) {
      this.summaryPriceEachEl.textContent = this.appendCada(textOverride);
      return;
    }

    if (!Number.isFinite(unitPrice)) {
      return;
    }
    this.summaryPriceEachEl.textContent = `${this.formatPrice(unitPrice)} cada`;
  }

  updateDiscountBadge(discountPercent) {
    if (!this.discountBadgeEl) {
      return;
    }

    if (discountPercent > 0) {
      this.discountBadgeEl.textContent = `${this.formatPercentage(discountPercent)}% OFF`;
      this.discountBadgeEl.removeAttribute('hidden');
    } else {
      this.discountBadgeEl.setAttribute('hidden', 'hidden');
    }
  }

  updateInstallments(totalCurrentPrice) {
    if (!this.installmentWrapper || !this.installmentCountEl || !this.installmentValueEl) {
      return;
    }

    if (this.maxInstallments <= 1 || totalCurrentPrice === null || Number.isNaN(totalCurrentPrice)) {
      this.installmentWrapper.setAttribute('hidden', 'hidden');
      return;
    }

    const totalCents = Math.round(totalCurrentPrice * 100);
    const minInstallmentCents = this.minInstallmentCents || 0;

    let selectedInstallments = 0;
    let installmentValueCents = 0;

    for (let parcels = this.maxInstallments; parcels >= 2; parcels -= 1) {
      const valueCents = Math.floor(totalCents / parcels);
      if (valueCents >= minInstallmentCents) {
        selectedInstallments = parcels;
        installmentValueCents = valueCents;
        break;
      }
    }

    if (selectedInstallments > 0 && installmentValueCents > 0) {
      this.installmentCountEl.textContent = selectedInstallments.toString();
      this.installmentValueEl.textContent = this.formatPrice(installmentValueCents / 100);
      this.installmentWrapper.removeAttribute('hidden');
    } else {
      this.installmentWrapper.setAttribute('hidden', 'hidden');
    }
  }

  async addToCart() {
    // Prevenir múltiplas execuções
    if (this.buyButton.disabled || this.selectedQuantity === null) return;

    try {
      this.buyButton.disabled = true;
      this.buyButton.textContent = 'Adicionando...';

      // Buscar informações do produto
      const productData = await this.fetchProductData();
      const variantId = productData.variants[0].id; // Primeira variante disponível

      // Preparar dados do carrinho
      const cartData = {
        id: variantId,
        quantity: this.selectedQuantity
      };

      // Adicionar ao carrinho
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartData)
      });

      if (response.ok) {
        const responseData = await response.json();
        
        // Sucesso - mostrar feedback
        this.showSuccessFeedback();
        
        // Buscar estado atual do carrinho
        const cartState = await this.getCartState();
        
        // Trigger evento correto do Horizon
        this.triggerHorizonCartUpdate(cartState);
        
        // Backup: tentar atualizar cart-items-component diretamente
        this.updateCartItemsComponent();
        
        // Abrir drawer do carrinho após um delay maior para processar eventos
        setTimeout(() => {
          this.openCartDrawer();
        }, 500);

      } else {
        throw new Error('Erro ao adicionar produto ao carrinho');
      }

    } catch (error) {
      console.error('Erro:', error);
      this.showErrorFeedback();
    }
  }

  async fetchProductData() {
    // Primeiro tentar buscar dados do produto que já estão disponíveis na página
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      return window.ShopifyAnalytics.meta.product;
    }

    // Fallback: buscar via handle na URL
    const handle = this.getProductHandle();
    if (handle) {
      const response = await fetch(`/products/${handle}.js`);
      return await response.json();
    }

    throw new Error('Não foi possível obter dados do produto');
  }

  getProductHandle() {
    // Tentar obter o handle do produto da URL
    const path = window.location.pathname;
    const match = path.match(/\/products\/([^\/]+)/);
    return match ? match[1] : null;
  }

  showSuccessFeedback() {
    this.buyButton.textContent = '✓ Adicionado!';
    this.buyButton.style.background = '#4CAF50';
    
    // Restaurar botão após 2 segundos
    setTimeout(() => {
      this.restoreButton();
    }, 2000);
  }

  showErrorFeedback() {
    this.buyButton.textContent = 'Erro - Tente novamente';
    this.buyButton.style.background = '#f44336';
    
    // Restaurar botão após 3 segundos
    setTimeout(() => {
      this.restoreButton();
    }, 3000);
  }

  restoreButton() {
    this.buyButton.disabled = false;
    this.buyButton.textContent = this.originalButtonText;
    this.buyButton.style.background = this.originalButtonStyle || '';
  }

  restoreButton() {
    this.buyButton.disabled = false;
    this.buyButton.textContent = this.originalButtonText;
    this.buyButton.style.background = this.originalButtonStyle || '';
  }

  async getCartState() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar estado do carrinho:', error);
      return null;
    }
  }

  triggerHorizonCartUpdate(cartState) {
    if (!cartState) return;

    const eventData = {
      data: {
        itemCount: cartState.item_count,
        source: 'quantity-bundle-component',
        cart: cartState
      }
    };

    // Lista de possíveis nomes de eventos do Horizon
    const eventVariations = [
      'theme:cart-update',
      'cart:update',
      'cartUpdate', 
      'cart-update',
      'ThemeEvents.cartUpdate'
    ];

    try {
      // Tentar todos os possíveis nomes de eventos
      eventVariations.forEach(eventName => {
        const event = new CustomEvent(eventName, {
          detail: eventData,
          bubbles: true
        });
        
        document.dispatchEvent(event);
      });
      
      console.log('✅ Eventos de carrinho disparados com sucesso');

    } catch (error) {
      console.error('Erro ao disparar evento do Horizon:', error);
    }
  }

  updateCartItemsComponent() {
    try {
      const cartItemsComponent = document.querySelector('cart-items-component');
      if (cartItemsComponent) {
        // Tentar vários métodos possíveis
        if (typeof cartItemsComponent.refresh === 'function') {
          cartItemsComponent.refresh();
        } else if (typeof cartItemsComponent.updateCart === 'function') {
          cartItemsComponent.updateCart();
        } else if (typeof cartItemsComponent.render === 'function') {
          cartItemsComponent.render();
        } else {
          // Força re-render removendo e adicionando o componente
          const parent = cartItemsComponent.parentNode;
          const nextSibling = cartItemsComponent.nextSibling;
          parent.removeChild(cartItemsComponent);
          parent.insertBefore(cartItemsComponent, nextSibling);
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar cart-items-component:', error);
    }
  }

  openCartDrawer() {
    // Versão para Horizon theme
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer) {
      // Tentar método open() primeiro
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return;
      }
      
      // Fallback: tentar showDialog() diretamente
      if (typeof cartDrawer.showDialog === 'function') {
        cartDrawer.showDialog();
        return;
      }
      
      // Fallback: simular clique no botão de abertura
      const openButton = cartDrawer.querySelector('button[on\\:click="/open"]');
      if (openButton) {
        openButton.click();
        return;
      }
    }

    // Fallback para Dawn theme
    const cartDrawerDawn = document.querySelector('cart-drawer');
    if (cartDrawerDawn && typeof cartDrawerDawn.open === 'function') {
      cartDrawerDawn.open();
      return;
    }

    // Fallback final: trigger do ícone do carrinho
    const cartIcon = document.querySelector('#cart-icon-bubble, [data-cart-toggle], cart-icon');
    if (cartIcon) {
      cartIcon.click();
    }
  }
}

// Inicialização quando o DOM estiver pronto
function initQuantityBundles() {
  const bundles = document.querySelectorAll('.quantity-bundle');
  bundles.forEach(bundle => {
    new QuantityBundle(bundle);
  });
}

// Auto-inicializar
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuantityBundles);
} else {
  initQuantityBundles();
}

// Re-inicializar quando seções forem carregadas dinamicamente (Theme Editor)
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  const bundles = section.querySelectorAll('.quantity-bundle');
  bundles.forEach(bundle => {
    new QuantityBundle(bundle);
  });
});

// Limpar quando seções forem removidas
document.addEventListener('shopify:section:unload', (event) => {
  // Cleanup se necessário (event listeners são automaticamente removidos quando elementos são removidos do DOM)
});

// Exportar para uso global se necessário
window.QuantityBundle = QuantityBundle;

// Inicialização quando o DOM estiver pronto
function initQuantityBundles() {
  const bundles = document.querySelectorAll('.quantity-bundle');
  bundles.forEach(bundle => {
    new QuantityBundle(bundle);
  });
}

// Auto-inicializar
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuantityBundles);
} else {
  initQuantityBundles();
}

// Re-inicializar quando seções forem carregadas dinamicamente (Theme Editor)
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  const bundles = section.querySelectorAll('.quantity-bundle');
  bundles.forEach(bundle => {
    new QuantityBundle(bundle);
  });
});

// Limpar quando seções forem removidas
document.addEventListener('shopify:section:unload', (event) => {
  // Cleanup se necessário (event listeners são automaticamente removidos quando elementos são removidos do DOM)
});

// Exportar para uso global se necessário
window.QuantityBundle = QuantityBundle;
